import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../module_roots/AuthContext';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faBox,
    faStore,
    faSearch,
    faCheckCircle,
    faTimes,
    faSave,
    faPlus,
    faMinus,
    faCircle,
    faCheck,
    faNetworkWired,
    faServer,
    faExclamationTriangle,
    faSync,
    faTag,
    faBarcode,
    faGlobe,
    faCopy,
    faFileAlt,
    faTrashAlt,
    faAtom,
    faRocket,
    faBolt,
    faHandshake,
    faUsers,
    faShieldAlt,
    faChartLine,
    faCrown
} from '@fortawesome/free-solid-svg-icons';
import "../module_mechanics/Styles/CentralizePro.css";

// ==============================================
// TOAST NOTIFICATION COMPONENT
// ==============================================
const ToastNotification = ({ toast, setToast, translations }) => {
    const getIconForType = (type) => {
        switch (type) {
            case 'success': return faCheckCircle;
            case 'error': return faExclamationTriangle;
            case 'warning': return faExclamationTriangle;
            default: return faCheckCircle;
        }
    };

    const getTitleForType = (type) => {
        switch (type) {
            case 'success': return translations?.success || 'Success';
            case 'error': return translations?.error || 'Error';
            case 'warning': return translations?.warning || 'Warning';
            default: return '';
        }
    };

    if (!toast.show) return null;

    return (
        <div className={`ozcentr_toast-notification ${toast.type}`}>
            <div className="ozcentr_toast-content">
                <div className="ozcentr_toast-icon">
                    <FontAwesomeIcon icon={getIconForType(toast.type)} />
                </div>
                <div className="ozcentr_toast-text">
                    <div className="ozcentr_toast-title">{getTitleForType(toast.type)}</div>
                    <div className="ozcentr_toast-message">{toast.message}</div>
                </div>
                <button className="ozcentr_toast-close" onClick={() => setToast(prev => ({ ...prev, show: false }))}>
                    <FontAwesomeIcon icon={faTimes} />
                </button>
            </div>
        </div>
    );
};

// ==============================================
// ORBIT ANIMATION COMPONENT - Clean Minimal Design
// ==============================================
const OrbitAnimation = ({ selectedCount, totalShops, selectedShopCount }) => {
    const animationRef = useRef(null);

    useEffect(() => {
        const rings = document.querySelectorAll('.ozcentr-orbit-ring-modern');
        let angle = 0;
        
        const animate = () => {
            angle += 0.4;
            rings.forEach((ring, index) => {
                const speed = (index + 1) * 0.3;
                ring.style.transform = `rotate(${angle * speed * (index % 2 === 0 ? 1 : -1)}deg)`;
            });
            animationRef.current = requestAnimationFrame(animate);
        };
        
        animationRef.current = requestAnimationFrame(animate);
        return () => {
            if (animationRef.current) {
                cancelAnimationFrame(animationRef.current);
            }
        };
    }, []);

    const selectedPercentage = totalShops > 0 ? (selectedShopCount / totalShops) * 100 : 0;
    const productPercentage = selectedCount > 0 ? Math.min((selectedCount / 10) * 100, 100) : 0;

    return (
        <div className="ozcentr-orbit-wrapper">
            <div className="ozcentr-orbit-container-modern">
                <div 
                    className="ozcentr-orbit-glow-modern"
                    style={{
                        opacity: 0.2 + (selectedPercentage / 100) * 0.3,
                    }}
                />

                <div className="ozcentr-orbit-ring-modern ozcentr-orbit-ring-modern-1">
                    <div className="ozcentr-orbit-dot" style={{ backgroundColor: '#6366f1' }}>
                        <div className="ozcentr-orbit-dot-pulse" style={{ opacity: selectedPercentage / 100 }} />
                    </div>
                </div>
                
                <div className="ozcentr-orbit-ring-modern ozcentr-orbit-ring-modern-2">
                    <div className="ozcentr-orbit-dot" style={{ backgroundColor: '#8b5cf6' }}>
                        <div className="ozcentr-orbit-dot-pulse" style={{ opacity: selectedPercentage / 100 }} />
                    </div>
                </div>

                <div 
                    className="ozcentr-orbit-core-modern"
                    style={{
                        transform: `scale(${1 + (selectedPercentage / 100) * 0.1})`,
                    }}
                >
                    <div className="ozcentr-core-content">
                        <span className="ozcentr-core-number">{selectedShopCount}</span>
                        <span className="ozcentr-core-subtitle">Shops</span>
                    </div>
                </div>

                {selectedCount > 0 && (
                    <svg className="ozcentr-progress-ring" viewBox="0 0 100 100">
                        <circle
                            className="ozcentr-progress-ring-bg"
                            cx="50"
                            cy="50"
                            r="42"
                        />
                        <circle
                            className="ozcentr-progress-ring-fill"
                            cx="50"
                            cy="50"
                            r="42"
                            strokeDasharray={`${(productPercentage / 100) * 264} 264`}
                            strokeLinecap="round"
                        />
                    </svg>
                )}
            </div>

            {/* <div className="ozcentr-orbit-stats">
                <div className="ozcentr-orbit-stat">
                    <span className="ozcentr-orbit-stat-value">{selectedCount}</span>
                    <span className="ozcentr-orbit-stat-label">Products</span>
                </div>
                <div className="ozcentr-orbit-stat-divider" />
                <div className="ozcentr-orbit-stat">
                    <span className="ozcentr-orbit-stat-value">{selectedShopCount}</span>
                    <span className="ozcentr-orbit-stat-label">Shops</span>
                </div>
            </div> */}
        </div>
    );
};

// ==============================================
// MAIN COMPONENT
// ==============================================
const CentralizedAssignment = () => {
    const { user, currentShop } = useAuth();

    // Refs
    const searchInputRef = useRef(null);
    const toastTimerRef = useRef(null);
    const isSubmittingRef = useRef(false);

    // State
    const [products, setProducts] = useState([]);
    const [filteredProducts, setFilteredProducts] = useState([]);
    const [selectedProductIds, setSelectedProductIds] = useState([]);
    const [availableShops, setAvailableShops] = useState([]);
    const [selectedShopIds, setSelectedShopIds] = useState([]);
    const [loading, setLoading] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [selectAll, setSelectAll] = useState(false);
    const [toast, setToast] = useState({ show: false, type: '', message: '' });
    const [isSaving, setIsSaving] = useState(false);
    const [shopSearchTerm, setShopSearchTerm] = useState('');
    const [showDraftsList, setShowDraftsList] = useState(false);
    const [savedDrafts, setSavedDrafts] = useState([]);
    const [currentLanguage, setCurrentLanguage] = useState(() => {
        return localStorage.getItem('ourzest_language') || 'en';
    });
    const [translations, setTranslations] = useState({});
    const [darkMode, setDarkMode] = useState(() => {
        return localStorage.getItem('ourzest_darkMode') === 'true';
    });
    const [currentThemeColor, setCurrentThemeColor] = useState(() => {
        return localStorage.getItem('ourzest_theme') || '#6366f1';
    });

    const isRTL = currentLanguage === 'ur' || currentLanguage === 'ar';
    const draftsDropdownRef = useRef(null);

    // ============================================
    // TRANSLATOR
    // ============================================
    const createTranslator = () => {
        let cache = new Map();

        const loadFromCache = () => {
            try {
                const cached = localStorage.getItem('ourzest_translations');
                if (cached) {
                    const parsed = JSON.parse(cached);
                    Object.entries(parsed).forEach(([key, value]) => {
                        cache.set(key, value);
                    });
                }
            } catch (e) {
                console.log("No cache found in Centralized Form");
            }
        };

        const saveToCache = () => {
            try {
                const cacheObj = {};
                cache.forEach((value, key) => {
                    cacheObj[key] = value;
                });
                localStorage.setItem('ourzest_translations', JSON.stringify(cacheObj));
            } catch (e) {
                console.error("Error saving to cache:", e);
            }
        };

        const translateAPI = async (text, targetLang) => {
            if (targetLang === 'en') return text;

            const dict = {
                'ur': {
                    'Centralized Assignment': 'مرکزی تفویض',
                    'Assign products to multiple shops': 'مصنوعات کو متعدد شاپس میں تفویض کریں',
                    'Search products...': 'مصنوعات تلاش کریں...',
                    'Select All': 'سب منتخب کریں',
                    'Deselect All': 'سب ہٹائیں',
                    'No products found': 'کوئی مصنوعات نہیں ملی',
                    'No shops available': 'کوئی شاپ دستیاب نہیں',
                    'Save Assignment': 'تفویض محفوظ کریں',
                    'Saving...': 'محفوظ ہو رہا ہے...',
                    'Products': 'مصنوعات',
                    'Shops': 'شاپس',
                    'Selected': 'منتخب کردہ',
                    'of': 'میں سے',
                    'shop(s)': 'شاپ(یں)',
                    'Success': 'کامیابی',
                    'Error': 'خرابی',
                    'Warning': 'انتباہ',
                    'Info': 'معلومات',
                    'Centralized in': 'مرکزی کردہ',
                    'Qty': 'مقدار',
                    'Loading products...': 'مصنوعات لوڈ ہو رہی ہیں...',
                    'Try adjusting your search': 'اپنی تلاش کو ایڈجسٹ کریں',
                    'Please select at least one product': 'براہ کرم کم از کم ایک مصنوعات منتخب کریں',
                    'Please select at least one shop': 'براہ کرم کم از کم ایک شاپ منتخب کریں',
                    'Failed to load products': 'مصنوعات لوڈ کرنے میں ناکامی',
                    'Network error': 'نیٹ ورک کی خرابی',
                    'Draft saved': 'ڈرافٹ محفوظ ہو گیا',
                    'Draft loaded': 'ڈرافٹ لوڈ ہو گیا',
                    'Draft cleared': 'ڈرافٹ صاف ہو گیا',
                    'Save Draft': 'ڈرافٹ محفوظ کریں',
                    'Drafts': 'ڈرافٹس',
                    'Saved Drafts': 'محفوظ کردہ ڈرافٹس',
                    'No drafts saved': 'کوئی ڈرافٹ محفوظ نہیں',
                    'Clear': 'صاف کریں',
                    'Clear Form': 'فارم صاف کریں',
                    'Current': 'موجودہ',
                    'Select Shops': 'شاپس منتخب کریں',
                    'Search shops...': 'شاپس تلاش کریں...',
                    'No shops found': 'کوئی شاپ نہیں ملی',
                    'Please contact admin to assign shops': 'براہ کرم شاپس تفویض کرنے کے لیے ایڈمن سے رابطہ کریں',
                    'Centralized': 'مرکزی',
                    'No shop selected': 'کوئی شاپ منتخب نہیں',
                    'Remove from Centralization': 'مرکزیت سے ہٹائیں',
                    'Removing...': 'ہٹایا جا رہا ہے...',
                    'Remove': 'ہٹائیں',
                    'Remove Selected': 'منتخب کردہ ہٹائیں',
                    'Confirm Remove': 'کیا آپ واقعی ان مصنوعات کو مرکزیت سے ہٹانا چاہتے ہیں؟',
                    'Removed Successfully': 'مصنوعات کامیابی سے مرکزیت سے ہٹا دی گئیں',
                    'Remove Failed': 'مصنوعات کو مرکزیت سے ہٹانے میں ناکامی'
                },
                'ar': {
                    'Centralized Assignment': 'التخصيص المركزي',
                    'Assign products to multiple shops': 'تخصيص المنتجات لمتاجر متعددة',
                    'Search products...': 'ابحث عن المنتجات...',
                    'Select All': 'تحديد الكل',
                    'Deselect All': 'إلغاء تحديد الكل',
                    'No products found': 'لم يتم العثور على منتجات',
                    'No shops available': 'لا توجد متاجر متاحة',
                    'Save Assignment': 'حفظ التخصيص',
                    'Saving...': 'جاري الحفظ...',
                    'Products': 'المنتجات',
                    'Shops': 'المتاجر',
                    'Selected': 'المحدد',
                    'of': 'من',
                    'shop(s)': 'متجر(متاجر)',
                    'Success': 'نجاح',
                    'Error': 'خطأ',
                    'Warning': 'تحذير',
                    'Info': 'معلومات',
                    'Centralized in': 'مركزي في',
                    'Qty': 'الكمية',
                    'Loading products...': 'جاري تحميل المنتجات...',
                    'Try adjusting your search': 'حاول تعديل بحثك',
                    'Please select at least one product': 'يرجى تحديد منتج واحد على الأقل',
                    'Please select at least one shop': 'يرجى تحديد متجر واحد على الأقل',
                    'Failed to load products': 'فشل في تحميل المنتجات',
                    'Network error': 'خطأ في الشبكة',
                    'Draft saved': 'تم حفظ المسودة',
                    'Draft loaded': 'تم تحميل المسودة',
                    'Draft cleared': 'تم مسح المسودة',
                    'Save Draft': 'حفظ المسودة',
                    'Drafts': 'المسودات',
                    'Saved Drafts': 'المسودات المحفوظة',
                    'No drafts saved': 'لا توجد مسودات محفوظة',
                    'Clear': 'مسح',
                    'Clear Form': 'مسح النموذج',
                    'Current': 'حالي',
                    'Select Shops': 'اختر المتاجر',
                    'Search shops...': 'ابحث عن المتاجر...',
                    'No shops found': 'لم يتم العثور على متاجر',
                    'Please contact admin to assign shops': 'يرجى الاتصال بالمسؤول لتخصيص المتاجر',
                    'Centralized': 'مركزي',
                    'No shop selected': 'لم يتم اختيار متجر',
                    'Remove from Centralization': 'إزالة من المركزية',
                    'Removing...': 'جاري الإزالة...',
                    'Remove': 'إزالة',
                    'Remove Selected': 'إزالة المحدد',
                    'Confirm Remove': 'هل أنت متأكد أنك تريد إزالة هذه المنتجات من المركزية؟',
                    'Removed Successfully': 'تم إزالة المنتجات من المركزية بنجاح',
                    'Remove Failed': 'فشل في إزالة المنتجات من المركزية'
                },
                'es': {
                    'Centralized Assignment': 'Asignación Centralizada',
                    'Assign products to multiple shops': 'Asignar productos a múltiples tiendas',
                    'Search products...': 'Buscar productos...',
                    'Select All': 'Seleccionar Todo',
                    'Deselect All': 'Deseleccionar Todo',
                    'No products found': 'No se encontraron productos',
                    'No shops available': 'No hay tiendas disponibles',
                    'Save Assignment': 'Guardar Asignación',
                    'Saving...': 'Guardando...',
                    'Products': 'Productos',
                    'Shops': 'Tiendas',
                    'Selected': 'Seleccionado',
                    'of': 'de',
                    'shop(s)': 'tienda(s)',
                    'Success': 'Éxito',
                    'Error': 'Error',
                    'Warning': 'Advertencia',
                    'Info': 'Información',
                    'Centralized in': 'Centralizado en',
                    'Qty': 'Cantidad',
                    'Loading products...': 'Cargando productos...',
                    'Try adjusting your search': 'Intente ajustar su búsqueda',
                    'Please select at least one product': 'Por favor seleccione al menos un producto',
                    'Please select at least one shop': 'Por favor seleccione al menos una tienda',
                    'Failed to load products': 'Error al cargar productos',
                    'Network error': 'Error de red',
                    'Draft saved': 'Borrador guardado',
                    'Draft loaded': 'Borrador cargado',
                    'Draft cleared': 'Borrador limpiado',
                    'Save Draft': 'Guardar Borrador',
                    'Drafts': 'Borradores',
                    'Saved Drafts': 'Borradores Guardados',
                    'No drafts saved': 'No hay borradores guardados',
                    'Clear': 'Limpiar',
                    'Clear Form': 'Limpiar Formulario',
                    'Current': 'Actual',
                    'Select Shops': 'Seleccionar Tiendas',
                    'Search shops...': 'Buscar tiendas...',
                    'No shops found': 'No se encontraron tiendas',
                    'Please contact admin to assign shops': 'Por favor contacte al administrador para asignar tiendas',
                    'Centralized': 'Centralizado',
                    'No shop selected': 'No se seleccionó ninguna tienda',
                    'Remove from Centralization': 'Eliminar de Centralización',
                    'Removing...': 'Eliminando...',
                    'Remove': 'Eliminar',
                    'Remove Selected': 'Eliminar Seleccionados',
                    'Confirm Remove': '¿Estás seguro de que quieres eliminar estos productos de la centralización?',
                    'Removed Successfully': 'Productos eliminados de centralización exitosamente',
                    'Remove Failed': 'Error al eliminar productos de centralización'
                }
            };

            if (dict[targetLang] && dict[targetLang][text]) {
                return dict[targetLang][text];
            }

            try {
                const response = await fetch('https://translate.argosopentech.com/translate', {
                    method: 'POST',
                    body: JSON.stringify({ q: text, source: 'en', target: targetLang }),
                    headers: { 'Content-Type': 'application/json' }
                });

                if (response.ok) {
                    const data = await response.json();
                    return data.translatedText;
                }
            } catch (e) {
                console.log("LibreTranslate failed, trying MyMemory");
            }

            try {
                const response = await fetch(
                    `https://api.mymemory.translated.net/get?q=${encodeURIComponent(text)}&langpair=en|${targetLang}`
                );
                if (response.ok) {
                    const data = await response.json();
                    return data.responseData.translatedText;
                }
            } catch (e) {
                console.log("MyMemory translation failed");
            }

            return text;
        };

        const translateText = async (text, targetLang) => {
            const cacheKey = `${text}_${targetLang}`;

            if (cache.has(cacheKey)) {
                return cache.get(cacheKey);
            }

            const translated = await translateAPI(text, targetLang);
            cache.set(cacheKey, translated);
            saveToCache();

            return translated;
        };

        const defaultTexts = {
            centralizedAssignment: 'Centralized Assignment',
            assignProducts: 'Assign products to multiple shops',
            searchProducts: 'Search products...',
            selectAll: 'Select All',
            deselectAll: 'Deselect All',
            noProducts: 'No products found',
            noShops: 'No shops available',
            saveAssignment: 'Save Assignment',
            saving: 'Saving...',
            products: 'Products',
            shops: 'Shops',
            selected: 'Selected',
            of: 'of',
            shopCount: 'shop(s)',
            success: 'Success',
            error: 'Error',
            warning: 'Warning',
            info: 'Info',
            centralizedIn: 'Centralized in',
            qty: 'Qty',
            loadingProducts: 'Loading products...',
            adjustSearch: 'Try adjusting your search',
            selectProduct: 'Please select at least one product',
            selectShop: 'Please select at least one shop',
            failedLoad: 'Failed to load products',
            networkError: 'Network error',
            draftSaved: 'Draft saved',
            draftLoaded: 'Draft loaded',
            draftCleared: 'Draft cleared',
            saveDraft: 'Save Draft',
            drafts: 'Drafts',
            savedDrafts: 'Saved Drafts',
            noDrafts: 'No drafts saved',
            clear: 'Clear',
            clearForm: 'Clear Form',
            current: 'Current',
            selectShops: 'Select Shops',
            searchShops: 'Search shops...',
            noShopsFound: 'No shops found',
            contactAdmin: 'Please contact admin to assign shops',
            centralized: 'Centralized',
            noShopSelected: 'No shop selected',
            removeFromCentralization: 'Remove from Centralization',
            removing: 'Removing...',
            remove: 'Remove',
            removeSelected: 'Remove Selected',
            confirmRemove: 'Are you sure you want to remove these products from centralization?',
            removedSuccess: 'Products removed from centralization successfully',
            removeFailed: 'Failed to remove products from centralization'
        };

        const getTranslations = async (lang) => {
            if (lang === 'en') {
                return defaultTexts;
            }

            const translations = {};
            const promises = [];

            Object.keys(defaultTexts).forEach(key => {
                const cacheKey = `${defaultTexts[key]}_${lang}`;
                if (cache.has(cacheKey)) {
                    translations[key] = cache.get(cacheKey);
                } else {
                    promises.push(
                        translateText(defaultTexts[key], lang).then(translated => {
                            translations[key] = translated;
                        })
                    );
                }
            });

            if (promises.length > 0) {
                await Promise.all(promises);
                saveToCache();
            }

            return translations;
        };

        loadFromCache();

        return {
            getTranslations,
            translateText
        };
    };

    const translator = createTranslator();

    // ============================================
    // THEME SYNC
    // ============================================
    const applyTheme = useCallback((color) => {
        if (!color || !/^#[0-9A-F]{6}$/i.test(color)) {
            color = '#6366f1';
        }
        document.documentElement.style.setProperty('--ozcentr-primary', color);
        document.documentElement.style.setProperty('--ozcentr-primary-dark', color);
        document.documentElement.style.setProperty('--ozcentr-primary-light', color);
        
        let r, g, b;
        if (color.startsWith('#')) {
            r = parseInt(color.slice(1, 3), 16);
            g = parseInt(color.slice(3, 5), 16);
            b = parseInt(color.slice(5, 7), 16);
        } else {
            r = 99; g = 102; b = 241;
        }
        
        let style = document.getElementById('ozcentr-dynamic-orb-styles');
        if (!style) {
            style = document.createElement('style');
            style.id = 'ozcentr-dynamic-orb-styles';
            document.head.appendChild(style);
        }
        
        style.textContent = `
            .ozui-header-bg-animation .gradient-orb.orb-1 {
                background: radial-gradient(circle, rgba(${r}, ${g}, ${b}, 0.4) 0%, rgba(${r}, ${g}, ${b}, 0.1) 100%);
            }
            .ozui-header-bg-animation .gradient-orb.orb-2 {
                background: radial-gradient(circle, rgba(${r}, ${g}, ${b}, 0.35) 0%, rgba(${r}, ${g}, ${b}, 0.08) 100%);
            }
            .ozui-header-bg-animation .gradient-orb.orb-3 {
                background: radial-gradient(circle, rgba(${r}, ${g}, ${b}, 0.3) 0%, rgba(${r}, ${g}, ${b}, 0.05) 100%);
            }
            .ozui-header-bg-animation .gradient-orb.orb-4 {
                background: radial-gradient(circle, rgba(${r}, ${g}, ${b}, 0.25) 0%, rgba(${r}, ${g}, ${b}, 0.03) 100%);
            }
            .ozui-header-bg-animation .gradient-orb.orb-5 {
                background: radial-gradient(circle, rgba(${r}, ${g}, ${b}, 0.2) 0%, rgba(${r}, ${g}, ${b}, 0.02) 100%);
            }
        `;
        setCurrentThemeColor(color);
    }, []);

    // ============================================
    // DARK MODE SYNC
    // ============================================
   // In CentralizedAssignment component - update the dark mode useEffect

useEffect(() => {
    const syncDarkMode = () => {
        const isDarkMode = localStorage.getItem('ourzest_darkMode') === 'true';
        setDarkMode(isDarkMode);
        
        // Only toggle dark class on the container for header styling
        const container = document.querySelector('.ozcentr-container');
        if (container) {
            if (isDarkMode) {
                container.classList.add('ozcentr-dark');
            } else {
                container.classList.remove('ozcentr-dark');
            }
        }
        
        // DO NOT add dark classes to body/html - this prevents global dark mode
        // Remove these lines:
        // document.body.classList.add('ozcentr-dark');
        // document.documentElement.classList.add('ozcentr-dark');
        
        // Instead, only add a class to the header element
        const header = document.querySelector('.ozcentr-sticky-header');
        if (header) {
            if (isDarkMode) {
                header.classList.add('ozcentr-header-dark');
            } else {
                header.classList.remove('ozcentr-header-dark');
            }
        }
    };
    
    syncDarkMode();
    
    const handleStorageChange = (e) => {
        if (e.key === 'ourzest_darkMode') {
            syncDarkMode();
        }
    };
    
    const handleDarkModeToggle = (event) => {
        if (event.detail) {
            const isDark = event.detail.darkMode;
            setDarkMode(isDark);
            
            const container = document.querySelector('.ozcentr-container');
            if (container) {
                if (isDark) {
                    container.classList.add('ozcentr-dark');
                } else {
                    container.classList.remove('ozcentr-dark');
                }
            }
            
            // Only update header
            const header = document.querySelector('.ozcentr-sticky-header');
            if (header) {
                if (isDark) {
                    header.classList.add('ozcentr-header-dark');
                } else {
                    header.classList.remove('ozcentr-header-dark');
                }
            }
        } else {
            syncDarkMode();
        }
    };
    
    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('darkModeToggled', handleDarkModeToggle);
    
    const interval = setInterval(() => {
        const storedDarkMode = localStorage.getItem('ourzest_darkMode') === 'true';
        if (storedDarkMode !== darkMode) {
            syncDarkMode();
        }
    }, 1000);
    
    return () => {
        window.removeEventListener('storage', handleStorageChange);
        window.removeEventListener('darkModeToggled', handleDarkModeToggle);
        clearInterval(interval);
    };
}, [darkMode]);

    // ============================================
    // LANGUAGE SYNC
    // ============================================
    useEffect(() => {
        const loadTranslations = async (lang) => {
            const trans = await translator.getTranslations(lang);
            setTranslations(trans);
        };
        
        const handleLanguageChange = () => {
            const lang = localStorage.getItem('ourzest_language') || 'en';
            if (lang !== currentLanguage) {
                setCurrentLanguage(lang);
                loadTranslations(lang);
            }
        };
        
        const handleCustomLanguageChange = (event) => {
            if (event.detail && event.detail.language) {
                const newLang = event.detail.language;
                if (newLang !== currentLanguage) {
                    setCurrentLanguage(newLang);
                    loadTranslations(newLang);
                }
            }
        };
        
        loadTranslations(currentLanguage);
        
        window.addEventListener('storage', handleLanguageChange);
        window.addEventListener('languageChanged', handleCustomLanguageChange);
        
        const interval = setInterval(() => {
            const storedLang = localStorage.getItem('ourzest_language') || 'en';
            if (storedLang !== currentLanguage) {
                setCurrentLanguage(storedLang);
                loadTranslations(storedLang);
            }
        }, 1000);
        
        return () => {
            window.removeEventListener('storage', handleLanguageChange);
            window.removeEventListener('languageChanged', handleCustomLanguageChange);
            clearInterval(interval);
        };
    }, [currentLanguage]);

    // ============================================
    // RTL SYNC
    // ============================================
    useEffect(() => {
        const isRTL = currentLanguage === 'ur' || currentLanguage === 'ar';
        const container = document.querySelector('.ozcentr-container');
        
        if (container) {
            if (isRTL) {
                container.classList.add('ozcentr-rtl');
                container.classList.remove('ozcentr-ltr');
            } else {
                container.classList.add('ozcentr-ltr');
                container.classList.remove('ozcentr-rtl');
            }
        }
        
        if (isRTL) {
            document.documentElement.dir = 'rtl';
            document.body.dir = 'rtl';
        } else {
            document.documentElement.dir = 'ltr';
            document.body.dir = 'ltr';
        }
    }, [currentLanguage]);

    // ============================================
    // THEME INIT
    // ============================================
    useEffect(() => {
        const savedTheme = localStorage.getItem('ourzest_theme') || '#6366f1';
        applyTheme(savedTheme);
        
        const handleStorageChange = (e) => {
            if (e.key === 'ourzest_theme') {
                const newTheme = e.newValue || '#6366f1';
                applyTheme(newTheme);
            }
        };
        
        const handleThemeChanged = (event) => {
            if (event.detail && event.detail.theme) {
                applyTheme(event.detail.theme);
            }
        };
        
        window.addEventListener('storage', handleStorageChange);
        window.addEventListener('themeChanged', handleThemeChanged);
        
        const interval = setInterval(() => {
            const storedTheme = localStorage.getItem('ourzest_theme');
            if (storedTheme && storedTheme !== currentThemeColor) {
                applyTheme(storedTheme);
            }
        }, 500);
        
        return () => {
            window.removeEventListener('storage', handleStorageChange);
            window.removeEventListener('themeChanged', handleThemeChanged);
            clearInterval(interval);
        };
    }, [applyTheme, currentThemeColor]);

    // ============================================
    // TOAST
    // ============================================
    const showToast = useCallback((type, message) => {
        if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
        setToast({ show: true, type, message });
        toastTimerRef.current = setTimeout(() => {
            setToast({ show: false, type: '', message: '' });
        }, 3000);
    }, []);

    useEffect(() => {
        return () => {
            if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
            isSubmittingRef.current = false;
        };
    }, []);

    // ============================================
    // DRAFTS
    // ============================================
    const loadDrafts = () => {
        const drafts = JSON.parse(localStorage.getItem('centralized_drafts') || '[]');
        const filteredDrafts = currentShop ? drafts.filter(d => d.shop_id === currentShop?.shop_id) : drafts;
        setSavedDrafts(filteredDrafts);
    };

    useEffect(() => {
        loadDrafts();
    }, [currentShop]);

    const saveDraft = () => {
        if (selectedProductIds.length === 0 && selectedShopIds.length === 0) {
            showToast('warning', 'Cannot save empty draft');
            return;
        }
        
        const draftId = Date.now();
        const draft = {
            id: draftId,
            selectedProductIds,
            selectedShopIds,
            savedAt: new Date().toISOString(),
            shop_id: currentShop?.shop_id
        };
        
        const existingDrafts = JSON.parse(localStorage.getItem('centralized_drafts') || '[]');
        existingDrafts.unshift(draft);
        const limitedDrafts = existingDrafts.slice(0, 20);
        localStorage.setItem('centralized_drafts', JSON.stringify(limitedDrafts));
        
        showToast('success', translations.draftSaved || 'Draft saved successfully!');
        loadDrafts();
    };

    const loadDraft = (draft) => {
        setSelectedProductIds(draft.selectedProductIds || []);
        setSelectedShopIds(draft.selectedShopIds || []);
        setSelectAll(false);
        setShowDraftsList(false);
        showToast('success', translations.draftLoaded || 'Draft loaded successfully!');
    };

    const deleteDraft = (draftId) => {
        const drafts = JSON.parse(localStorage.getItem('centralized_drafts') || '[]');
        const filteredDrafts = drafts.filter(d => d.id !== draftId);
        localStorage.setItem('centralized_drafts', JSON.stringify(filteredDrafts));
        loadDrafts();
        showToast('success', 'Draft deleted successfully!');
    };

    const clearForm = () => {
        setSelectedProductIds([]);
        setSelectedShopIds([]);
        setSelectAll(false);
        setSearchTerm('');
        setShopSearchTerm('');
        showToast('success', translations.draftCleared || 'Form cleared');
    };

    // ============================================
    // DATA FETCHING
    // ============================================
    const fetchUserCentralizedShops = useCallback(async () => {
        if (!user?.id) {
            return [];
        }

        try {
            const params = new URLSearchParams({
                action: 'get_all_shops',
                current_user_id: String(user.id),
                current_user_role: user.role || '',
                centralized_only: '1'
            });

            const response = await fetch(`https://ourzestapp.online/oz_backend/root_users.php?${params}&t=${Date.now()}`);
            const data = await response.json();

            if (data.success && data.data && data.data.length > 0) {
                setAvailableShops(data.data);
                const allShopIds = data.data.map(shop => shop.shop_id);
                setSelectedShopIds(allShopIds);
                return data.data;
            }

            setAvailableShops([]);
            setSelectedShopIds([]);
            showToast('info', translations.noShops || 'No centralized shops assigned. Please contact admin.');
            return [];

        } catch (error) {
            console.error('Error fetching centralized shops:', error);
            showToast('error', 'Failed to load centralized shops: ' + error.message);
            return [];
        }
    }, [user?.id, user?.role, translations]);

    const fetchProducts = useCallback(async (search = '') => {
        if (!currentShop) {
            showToast('error', translations.noShopSelected || 'Please select a shop first');
            return;
        }

        if (!user?.username) {
            showToast('error', 'User not authenticated');
            return;
        }

        setLoading(true);
        try {
            const requestData = {
                action: 'get_records',
                username: user.username,
                shop_id: currentShop.shop_id
            };
            
            const response = await fetch('https://ourzestapp.online/oz_backend/cosmetics_module/cosmetics_products.php', {
                method: 'POST',
                headers: { 
                    'Content-Type': 'application/json',
                    'Accept': 'application/json'
                },
                body: JSON.stringify(requestData)
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const data = await response.json();
            
            if (data.status === 'success') {
                let productList = data.data || [];
                
                if (search.trim() !== '') {
                    const lowerTerm = search.toLowerCase();
                    productList = productList.filter(p =>
                        p.product_name?.toLowerCase().includes(lowerTerm) ||
                        p.batch_no?.toLowerCase().includes(lowerTerm) ||
                        p.barcode?.toLowerCase().includes(lowerTerm) ||
                        p.product_code?.toLowerCase().includes(lowerTerm)
                    );
                }
                
                setProducts(productList);
                setFilteredProducts(productList);
                setSelectedProductIds([]);
                setSelectAll(false);
            } else {
                showToast('error', data.message || translations.failedLoad || 'Failed to load products');
            }
        } catch (error) {
            console.error('Error fetching products:', error);
            showToast('error', `${translations.networkError || 'Network error'}: ${error.message}`);
        } finally {
            setLoading(false);
        }
    }, [currentShop, user?.username, translations]);

    // ============================================
    // SAVE CENTRALIZED ASSIGNMENT
    // ============================================
    const handleSave = async () => {
        if (selectedProductIds.length === 0) {
            showToast('warning', translations.selectProduct || 'Please select at least one product');
            return;
        }

        if (selectedShopIds.length === 0) {
            showToast('warning', translations.selectShop || 'Please select at least one shop');
            return;
        }

        setIsSaving(true);
        isSubmittingRef.current = true;
        
        try {
            let successCount = 0;
            let errorCount = 0;

            for (const productId of selectedProductIds) {
                try {
                    const response = await fetch('https://ourzestapp.online/oz_backend/cosmetics_module/cosmetics_products.php', {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({
                            action: 'update_centralized',
                            product_id: productId,
                            is_centralized: 1,
                            shop_ids: selectedShopIds,
                            username: user?.username
                        })
                    });

                    const data = await response.json();
                    if (data.status === 'success') {
                        successCount++;
                    } else {
                        errorCount++;
                    }
                } catch (err) {
                    errorCount++;
                }
            }

            if (successCount > 0) {
                showToast('success', `Centralized ${successCount} product(s)${errorCount > 0 ? `, ${errorCount} failed` : ''}`);
                fetchProducts(searchTerm);
                setSelectedProductIds([]);
                setSelectAll(false);
            } else {
                showToast('error', 'Failed to centralize products. Please try again.');
            }
        } catch (error) {
            console.error('Error saving centralized assignment:', error);
            showToast('error', 'Network error while saving');
        } finally {
            setIsSaving(false);
            isSubmittingRef.current = false;
        }
    };

    // ============================================
    // REMOVE FROM CENTRALIZATION
    // ============================================
    const handleRemoveFromCentralization = async (productIds) => {
        if (!productIds || productIds.length === 0) {
            showToast('warning', 'Please select products to remove');
            return;
        }

        const confirmMessage = translations.confirmRemove || `Are you sure you want to remove ${productIds.length} product(s) from centralization?`;
        if (!window.confirm(confirmMessage)) {
            return;
        }

        setIsSaving(true);
        
        try {
            const response = await fetch('https://ourzestapp.online/oz_backend/cosmetics_module/cosmetics_products.php', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    action: 'remove_from_centralized',
                    product_ids: productIds,
                    username: user?.username
                })
            });

            const data = await response.json();
            
            if (data.status === 'success') {
                showToast('success', data.message || translations.removedSuccess || 'Products removed from centralization successfully');
                fetchProducts(searchTerm);
                setSelectedProductIds([]);
                setSelectAll(false);
            } else {
                showToast('error', data.message || translations.removeFailed || 'Failed to remove products from centralization');
            }
        } catch (error) {
            console.error('Error removing from centralization:', error);
            showToast('error', translations.networkError || 'Network error while removing from centralization');
        } finally {
            setIsSaving(false);
        }
    };

    // ============================================
    // REMOVE SPECIFIC SHOPS FROM PRODUCT
    // ============================================
    const handleRemoveShopsFromProduct = async (productId, shopIdsToRemove) => {
        if (!productId || !shopIdsToRemove || shopIdsToRemove.length === 0) {
            showToast('warning', 'Please select shops to remove');
            return;
        }

        if (!window.confirm(`Remove ${shopIdsToRemove.length} shop(s) from this product's centralization?`)) {
            return;
        }

        setIsSaving(true);
        
        try {
            const response = await fetch('https://ourzestapp.online/oz_backend/cosmetics_module/cosmetics_products.php', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    action: 'remove_shops_from_centralized',
                    product_id: productId,
                    shop_ids: shopIdsToRemove,
                    username: user?.username
                })
            });

            const data = await response.json();
            
            if (data.status === 'success') {
                showToast('success', data.message || 'Shops removed from product successfully');
                fetchProducts(searchTerm);
            } else {
                showToast('error', data.message || 'Failed to remove shops from product');
            }
        } catch (error) {
            console.error('Error removing shops:', error);
            showToast('error', 'Network error while removing shops');
        } finally {
            setIsSaving(false);
        }
    };

    // ============================================
    // HANDLE BULK REMOVE ALL CENTRALIZED SELECTED
    // ============================================
    const handleRemoveSelectedFromCentralization = () => {
        if (selectedProductIds.length === 0) {
            showToast('warning', 'Please select at least one product');
            return;
        }
        
        // Filter to only centralized products
        const centralizedSelected = selectedProductIds.filter(id => {
            const product = filteredProducts.find(p => p.id === id);
            return product && product.is_product_centralized === 1;
        });
        
        if (centralizedSelected.length === 0) {
            showToast('warning', 'No centralized products selected');
            return;
        }
        
        handleRemoveFromCentralization(centralizedSelected);
    };

    // ============================================
    // LOAD DATA
    // ============================================
    useEffect(() => {
        const loadData = async () => {
            const shops = await fetchUserCentralizedShops();
            if (shops.length === 0) {
                showToast('info', translations.noShops || 'No centralized shops assigned. Please contact admin.');
            }
            if (currentShop) {
                fetchProducts();
            }
        };
        loadData();
    }, [currentShop, fetchUserCentralizedShops, fetchProducts]);

    // ============================================
    // HANDLERS
    // ============================================
    const handleSearch = (e) => {
        const term = e.target.value;
        setSearchTerm(term);
        fetchProducts(term);
    };

    const toggleProductSelection = (productId) => {
        setSelectedProductIds(prev => {
            if (prev.includes(productId)) {
                return prev.filter(id => id !== productId);
            } else {
                return [...prev, productId];
            }
        });
        setSelectAll(false);
    };

    const toggleSelectAll = () => {
        if (selectAll) {
            setSelectedProductIds([]);
        } else {
            const allIds = filteredProducts.map(p => p.id);
            setSelectedProductIds(allIds);
        }
        setSelectAll(!selectAll);
    };

    const toggleShopSelection = (shopId) => {
        setSelectedShopIds(prev => {
            if (prev.includes(shopId)) {
                return prev.filter(id => id !== shopId);
            } else {
                return [...prev, shopId];
            }
        });
    };

    const toggleSelectAllShops = () => {
        if (selectedShopIds.length === availableShops.length) {
            setSelectedShopIds([]);
        } else {
            setSelectedShopIds(availableShops.map(s => s.shop_id));
        }
    };

    const filteredShops = availableShops.filter(shop =>
        shop.shop_name?.toLowerCase().includes(shopSearchTerm.toLowerCase()) ||
        (shop.shop_code && shop.shop_code.toLowerCase().includes(shopSearchTerm.toLowerCase()))
    );

    const isProductCentralized = (product) => {
        return product.is_product_centralized === 1 && product.centralized;
    };

    const getProductCentralizedShops = (product) => {
        if (!product.centralized) return [];
        return product.centralized.split(',').map(id => parseInt(id.trim())).filter(id => !isNaN(id));
    };

    const selectedCount = selectedProductIds.length;
    const totalProducts = filteredProducts.length;

    // Check if any selected product is centralized
    const hasCentralizedSelected = selectedProductIds.some(id => {
        const product = filteredProducts.find(p => p.id === id);
        return product && product.is_product_centralized === 1;
    });

    // ============================================
    // RENDER
    // ============================================
    return (
        <div className={`ozcentr-container ${isRTL ? 'ozcentr-rtl' : 'ozcentr-ltr'} ${darkMode ? 'ozcentr-dark' : ''}`}>
            <ToastNotification toast={toast} setToast={setToast} translations={translations} />

            {/* Modern Animated Sticky Header */}
            <div className="ozcentr-sticky-header">
                <div className="ozcentr-header-bg-animation">
                    <div className="gradient-orb orb-1"></div>
                    <div className="gradient-orb orb-2"></div>
                    <div className="gradient-orb orb-3"></div>
                    <div className="gradient-orb orb-4"></div>
                    <div className="gradient-orb orb-5"></div>
                </div>
                <div className="ozcentr-header-content">
                    <div className="ozcentr-header-left">
                        <div className="ozcentr-header-title">
                            <h1>
                               
                                {translations.centralizedAssignmet || 'Stock Centralization'}
                            </h1>
                            <p>{translations.assignProducts || 'Assign products to multiple shops'}</p>
                        </div>
                    </div>
                    
                    <div className="ozcentr-header-right">
                        <div className="ozcentr-header-actions">
                            <button 
                                className="ozcentr-action-btn clear-draft" 
                                onClick={clearForm}
                                disabled={loading || isSaving}
                                title={translations.clearForm || 'Clear Form'}
                            >
                                <FontAwesomeIcon icon={faTrashAlt} />
                                <span>{translations.clear || 'Clear'}</span>
                            </button>
                            
                            <button 
                                className="ozcentr-action-btn save-draft" 
                                onClick={saveDraft}
                                disabled={loading || isSaving}
                                title={translations.saveDraft || 'Save as Draft'}
                            >
                                <FontAwesomeIcon icon={faCopy} />
                                <span>{translations.saveDraft || 'Save Draft'}</span>
                            </button>
                            
                            <div className="ozcentr-drafts-dropdown-container" ref={draftsDropdownRef}>
                                <button 
                                    className="ozcentr-action-btn drafts-btn" 
                                    onClick={() => setShowDraftsList(!showDraftsList)}
                                    title={translations.drafts || 'View Drafts'}
                                >
                                    <FontAwesomeIcon icon={faFileAlt} />
                                    <span>{translations.drafts || 'Drafts'}</span>
                                    {savedDrafts.length > 0 && <span className="drafts-badge">{savedDrafts.length}</span>}
                                </button>
                                
                                {showDraftsList && (
                                    <div className="ozcentr-drafts-dropdown">
                                        <div className="drafts-header">
                                            <h4>{translations.savedDrafts || 'Saved Drafts'}</h4>
                                            <button onClick={() => setShowDraftsList(false)}>×</button>
                                        </div>
                                        <div className="drafts-list">
                                            {savedDrafts.length === 0 ? (
                                                <div className="no-drafts">{translations.noDrafts || 'No drafts saved'}</div>
                                            ) : (
                                                savedDrafts.map(draft => (
                                                    <div key={draft.id} className="draft-item">
                                                        <div className="draft-info" onClick={() => loadDraft(draft)}>
                                                            <div className="draft-name">
                                                                {draft.selectedProductIds.length} products, {draft.selectedShopIds.length} shops
                                                            </div>
                                                            <div className="draft-date">
                                                                {new Date(draft.savedAt).toLocaleDateString()}
                                                            </div>
                                                        </div>
                                                        <button 
                                                            className="delete-draft-btn"
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                deleteDraft(draft.id);
                                                            }}
                                                        >
                                                            <FontAwesomeIcon icon={faTimes} />
                                                        </button>
                                                    </div>
                                                ))
                                            )}
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Stats Bar */}
            {/* <div className="ozcentr-stats">
                <div className="ozcentr-stat">
                    <span className="stat-label">
                        <FontAwesomeIcon icon={faBox} />
                        {translations.products || 'Products'}
                    </span>
                    <span className="stat-value">{totalProducts}</span>
                </div>
                <div className="ozcentr-stat">
                    <span className="stat-label">
                        <FontAwesomeIcon icon={faCheckCircle} />
                        {translations.selected || 'Selected'}
                    </span>
                    <span className="stat-value">{selectedCount}</span>
                </div>
                <div className="ozcentr-stat">
                    <span className="stat-label">
                        <FontAwesomeIcon icon={faStore} />
                        {translations.shops || 'Shops'}
                    </span>
                    <span className="stat-value">{availableShops.length}</span>
                </div>
                <div className="ozcentr-stat">
                    <span className="stat-label">
                        <FontAwesomeIcon icon={faCheck} />
                        {translations.selected || 'Selected'}
                    </span>
                    <span className="stat-value">{selectedShopIds.length}</span>
                </div>
            </div> */}

            {/* Main Content - Two Column Layout with adjusted widths */}
            <div className="ozcentr-main">
                {/* Left Panel - Products (reduced width) */}
                <div className="ozcentr-products-panel">
                    <div className="ozcentr-panel-header">
                        <div className="ozcentr-search-wrapper">
                            <FontAwesomeIcon icon={faSearch} className="ozcentr-search-icon" />
                            <input
                                ref={searchInputRef}
                                type="text"
                                placeholder={translations.searchProducts || 'Search products...'}
                                value={searchTerm}
                                onChange={handleSearch}
                                className="ozcentr-search-input"
                            />
                        </div>
                        <div className="ozcentr-panel-actions">
                            <button
                                className="ozcentr-select-all-btn"
                                onClick={toggleSelectAll}
                                disabled={filteredProducts.length === 0 || loading}
                            >
                                <FontAwesomeIcon icon={selectAll ? faMinus : faPlus} />
                                {selectAll ? (translations.deselectAll || 'Deselect All') : (translations.selectAll || 'Select All')}
                            </button>
                            
                            {/* Remove from Centralization Button - Only shows when centralized products are selected */}
                            {hasCentralizedSelected && (
                                <button
                                    className="ozcentr-remove-btn"
                                    onClick={handleRemoveSelectedFromCentralization}
                                    disabled={isSaving || loading}
                                    title={translations.removeFromCentralization || 'Remove from Centralization'}
                                >
                                    <FontAwesomeIcon icon={faTrashAlt} />
                                    <span>{translations.removeSelected || 'Remove Selected'}</span>
                                </button>
                            )}
                            
                            <button
                                className="ozcentr-refresh-btn"
                                onClick={() => fetchProducts(searchTerm)}
                                disabled={loading}
                            >
                                <FontAwesomeIcon icon={faSync} spin={loading} />
                            </button>
                        </div>
                    </div>

                    <div className="ozcentr-products-list">
                        {loading ? (
                            <div className="ozcentr-loading-state">
                                <div className="ozcentr-spinner"></div>
                                <span>{translations.loadingProducts || 'Loading products...'}</span>
                            </div>
                        ) : filteredProducts.length === 0 ? (
                            <div className="ozcentr-empty-state">
                                <FontAwesomeIcon icon={faBox} />
                                <p>{translations.noProducts || 'No products found'}</p>
                                {searchTerm && <small>{translations.adjustSearch || 'Try adjusting your search'}</small>}
                            </div>
                        ) : (
                            filteredProducts.map(product => {
                                const isSelected = selectedProductIds.includes(product.id);
                                const isCentralized = isProductCentralized(product);
                                const centralizedShops = getProductCentralizedShops(product);
                                
                                return (
                                    <div
                                        key={product.id}
                                        className={`ozcentr-product-item ${isSelected ? 'selected' : ''} ${isCentralized ? 'centralized' : ''}`}
                                        onClick={() => toggleProductSelection(product.id)}
                                    >
                                        <div className="ozcentr-product-check">
                                            <FontAwesomeIcon icon={isSelected ? faCheckCircle : faCircle} />
                                        </div>
                                        <div className="ozcentr-product-info">
                                            <div className="ozcentr-product-name">
                                                {product.product_name}
                                                {product.product_code && (
                                                    <span className="ozcentr-product-code">{product.product_code}</span>
                                                )}
                                            </div>
                                            <div className="ozcentr-product-meta">
                                                {product.batch_no && (
                                                    <span className="ozcentr-meta-tag">
                                                        <FontAwesomeIcon icon={faTag} />
                                                        {product.batch_no}
                                                    </span>
                                                )}
                                                {product.barcode && (
                                                    <span className="ozcentr-meta-tag">
                                                        <FontAwesomeIcon icon={faBarcode} />
                                                        {product.barcode}
                                                    </span>
                                                )}
                                                {product.category && (
                                                    <span className="ozcentr-meta-tag">
                                                        {product.category}
                                                    </span>
                                                )}
                                                <span className="ozcentr-meta-tag">
                                                    {translations.qty || 'Qty'}: {product.quantity || 0}
                                                </span>
                                            </div>
                                            {isCentralized && (
                                                <div className="ozcentr-product-status">
                                                    <FontAwesomeIcon icon={faServer} />
                                                    <span>{translations.centralizedIn || 'Centralized in'} {centralizedShops.length} {translations.shopCount || 'shop(s)'}</span>
                                                </div>
                                            )}
                                        </div>
                                        {isCentralized ? (
                                            <div className="ozcentr-product-centralized-actions">
                                                <div className="ozcentr-product-badge">
                                                    <FontAwesomeIcon icon={faCheckCircle} />
                                                    {translations.centralized || 'Centralized'}
                                                </div>
                                               
                                            </div>
                                        ) : (
                                            <div className="ozcentr-product-badge not-centralized">
                                                <FontAwesomeIcon icon={faCircle} />
                                                Not Centralized
                                            </div>
                                        )}
                                    </div>
                                );
                            })
                        )}
                    </div>

                    {filteredProducts.length > 0 && (
                        <div className="ozcentr-panel-footer">
                            <span>Showing {filteredProducts.length} {translations.products || 'products'}</span>
                            <span className="ozcentr-selected-info">
                                {selectedCount} {translations.selected || 'selected'}
                                {hasCentralizedSelected && (
                                    <span className="ozcentr-centralized-selected-badge">
                                        ({selectedProductIds.filter(id => {
                                            const product = filteredProducts.find(p => p.id === id);
                                            return product && product.is_product_centralized === 1;
                                        }).length} centralized)
                                    </span>
                                )}
                            </span>
                        </div>
                    )}
                </div>

                {/* Right Panel - Shops with Separated Animation */}
                <div className="ozcentr-shops-panel">
                    {/* Animation Section - Top */}
                    <div className="ozcentr-orbit-wrapper">
                        <OrbitAnimation 
                            selectedCount={selectedProductIds.length}
                            totalShops={availableShops.length}
                            selectedShopCount={selectedShopIds.length}
                        />
                    </div>

                    {/* Shop Selection Section - Bottom */}
                    <div className="ozcentr-shops-selection">
                        <div className="ozcentr-panel-header">
                            <h3>
                                <FontAwesomeIcon icon={faStore} />
                                {translations.selectShops || 'Select Shops'}
                                <span className="ozcentr-shop-count">
                                    {selectedShopIds.length} / {availableShops.length}
                                </span>
                            </h3>
                            <button
                                className="ozcentr-select-all-shops-btn"
                                onClick={toggleSelectAllShops}
                                disabled={availableShops.length === 0}
                            >
                                {selectedShopIds.length === availableShops.length && availableShops.length > 0
                                    ? (translations.deselectAll || 'Deselect All')
                                    : (translations.selectAll || 'Select All')}
                            </button>
                        </div>

                        {/* <div className="ozcentr-shops-search-wrapper">
                            <FontAwesomeIcon icon={faSearch} className="ozcentr-search-icon" />
                            <input
                                type="text"
                                placeholder={translations.searchShops || 'Search shops...'}
                                value={shopSearchTerm}
                                onChange={(e) => setShopSearchTerm(e.target.value)}
                                className="ozcentr-search-input"
                            />
                        </div> */}

                        <div className="ozcentr-shops-list">
                            {availableShops.length === 0 ? (
                                <div className="ozcentr-empty-shops">
                                    <FontAwesomeIcon icon={faExclamationTriangle} />
                                    <p>{translations.noShops || 'No centralized shops available'}</p>
                                    <small>{translations.contactAdmin || 'Please contact admin to assign shops'}</small>
                                </div>
                            ) : filteredShops.length === 0 ? (
                                <div className="ozcentr-empty-shops">
                                    <FontAwesomeIcon icon={faSearch} />
                                    <p>{translations.noShopsFound || 'No shops found'}</p>
                                </div>
                            ) : (
                                filteredShops.map(shop => {
                                    const isSelected = selectedShopIds.includes(shop.shop_id);
                                    return (
                                        <div
                                            key={shop.shop_id}
                                            className={`ozcentr-shop-item ${isSelected ? 'selected' : ''}`}
                                            onClick={() => toggleShopSelection(shop.shop_id)}
                                        >
                                            <div className="ozcentr-shop-check">
                                                <FontAwesomeIcon icon={isSelected ? faCheckCircle : faCircle} />
                                            </div>
                                            <div className="ozcentr-shop-info">
                                                <div className="ozcentr-shop-name">{shop.shop_name}</div>
                                                <div className="ozcentr-shop-code">{shop.shop_code}</div>
                                            </div>
                                            {isSelected && (
                                                <div className="ozcentr-shop-badge-selected">
                                                    <FontAwesomeIcon icon={faCheck} />
                                                </div>
                                            )}
                                        </div>
                                    );
                                })
                            )}
                        </div>

                        <div className="ozcentr-panel-footer">
                            <div className="ozcentr-selection-summary">
                                <span>
                                    {selectedShopIds.length} {translations.of || 'of'} {availableShops.length} {translations.shops || 'shops'} {translations.selected || 'selected'}
                                </span>
                            </div>
                            <button
                                className={`ozcentr-save-btn ${selectedProductIds.length === 0 || selectedShopIds.length === 0 ? 'disabled' : ''}`}
                                onClick={handleSave}
                                disabled={selectedProductIds.length === 0 || selectedShopIds.length === 0 || isSaving || loading}
                            >
                                {isSaving ? (
                                    <>
                                        <div className="ozcentr-spinner-small"></div>
                                        {translations.saving || 'Saving...'}
                                    </>
                                ) : (
                                    <>
                                        <FontAwesomeIcon icon={faSave} />
                                        {translations.saveAssignment || 'Save Assignment'}
                                    </>
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default CentralizedAssignment;